import { z } from 'zod';
import { VIEWPORT_CLASSES } from '../events/journey-event.schema.js';

/**
 * Before/after comparison of a published configuration version (functional §5.4
 * "Comparaison avant et après", rule 4). The two windows are defined by the
 * measurement itself, so the filters carry no period: journey, language and
 * device only, one parameter per criterion (technical §11.2).
 */
export const comparisonFiltersSchema = z
  .object({
    journeyId: z.string().uuid().optional(),
    language: z.string().length(2).optional(),
    viewport: z.enum(VIEWPORT_CLASSES).optional(),
  })
  .strict();
export type ComparisonFilters = z.infer<typeof comparisonFiltersSchema>;

/** Closed list of compared indicators; the Console owns their wording. Mirrors the domain list. */
export const COMPARISON_INDICATORS = [
  'completion_rate',
  'drop_off_rate',
  'abandon_rate',
  'error_rate',
  'reentry_rate',
  'friction_score',
  'median_duration_ms',
  'hesitations_per_session',
  'errors_per_session',
  'rage_clicks_per_session',
] as const;
export type ComparisonIndicatorKey = (typeof COMPARISON_INDICATORS)[number];

export const indicatorDeltaDto = z.object({
  key: z.enum(COMPARISON_INDICATORS),
  unit: z.enum(['rate', 'score', 'ms', 'per_session']),
  beforeValue: z.number(),
  afterValue: z.number(),
  absoluteDelta: z.number(),
  /** Relative change against the before value; null when the before value is 0. */
  deltaRatio: z.number().nullable(),
  direction: z.enum(['improved', 'worsened', 'unchanged']),
  /** True when either side counts fewer sessions than the project minimum (functional §5.4 R1). */
  lowConfidence: z.boolean(),
});
export type IndicatorDelta = z.infer<typeof indicatorDeltaDto>;

/** A closed range of calendar days, ISO `YYYY-MM-DD`, both ends included. */
export const dayWindowDto = z.object({
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  /** Sessions that entered a journey on the side, after the filters. */
  sessions: z.number().int(),
});
export type DayWindow = z.infer<typeof dayWindowDto>;

/** A guidance published in the compared version: what the visitor started to see. */
export const comparedGuidanceDto = z.object({
  guidanceId: z.string().uuid(),
  journeyId: z.string().uuid().nullable(),
  journeyName: z.string().nullable(),
  stepId: z.string().uuid().nullable(),
  stepName: z.string().nullable(),
  component: z.string(),
});
export type ComparedGuidance = z.infer<typeof comparedGuidanceDto>;

export const comparisonSummaryDto = z.object({
  releaseVersion: z.number().int().min(1),
  publishedAt: z.string().datetime(),
  /** Length of each window, in days (functional §5.4 rule 4: equivalent duration). */
  days: z.number().int().min(1),
  before: dayWindowDto,
  after: dayWindowDto,
  guidances: z.array(comparedGuidanceDto),
  /** Headline deltas over every journey: completion rate and friction score. */
  completionRate: indicatorDeltaDto,
  frictionScore: indicatorDeltaDto,
  lowConfidence: z.boolean(),
});
export type ComparisonSummary = z.infer<typeof comparisonSummaryDto>;

export const comparisonListDto = z.object({
  items: z.array(comparisonSummaryDto),
  /** The version served today, whether or not it is comparable yet. */
  activeReleaseVersion: z.number().int().nullable(),
});
export type ComparisonList = z.infer<typeof comparisonListDto>;

export const comparedFieldDto = z.object({
  fieldKey: z.string(),
  /** The label the page gives the field, from the journey's step elements, when it has one. */
  fieldLabel: z.string().nullable(),
  beforeSessions: z.number().int(),
  afterSessions: z.number().int(),
  lowConfidence: z.boolean(),
  indicators: z.array(indicatorDeltaDto),
});
export type ComparedField = z.infer<typeof comparedFieldDto>;

export const comparedStepDto = z.object({
  stepId: z.string().uuid(),
  stepName: z.string(),
  position: z.number().int(),
  /** True when a guidance published in the compared version sits on this step. */
  hasGuidance: z.boolean(),
  beforeSessions: z.number().int(),
  afterSessions: z.number().int(),
  lowConfidence: z.boolean(),
  indicators: z.array(indicatorDeltaDto),
  fields: z.array(comparedFieldDto),
});
export type ComparedStep = z.infer<typeof comparedStepDto>;

export const comparedJourneyDto = z.object({
  journeyId: z.string().uuid(),
  journeyName: z.string(),
  beforeSessions: z.number().int(),
  afterSessions: z.number().int(),
  lowConfidence: z.boolean(),
  /** Journey-level indicators: completion rate and friction score of the journey. */
  indicators: z.array(indicatorDeltaDto),
  steps: z.array(comparedStepDto),
});
export type ComparedJourney = z.infer<typeof comparedJourneyDto>;

export const comparisonDto = comparisonSummaryDto.extend({
  journeys: z.array(comparedJourneyDto),
  dimensions: z.object({
    languages: z.array(z.string()),
    viewports: z.array(z.enum(VIEWPORT_CLASSES)),
  }),
  minimumSessionCount: z.number().int(),
});
export type Comparison = z.infer<typeof comparisonDto>;
